import { useEffect, useState, useRef } from 'react';

// Safe DOM manipulation utilities
const safeRemoveChild = (parent, child) => {
  try {
    if (parent && child && child.parentNode === parent) {
      parent.removeChild(child);
    }
  } catch (error) {
    console.warn('Safe removeChild failed:', error);
  }
};

const safeAppendChild = (parent, child) => {
  try {
    if (parent && child) {
      parent.appendChild(child);
    }
  } catch (error) {
    console.warn('Safe appendChild failed:', error);
  }
};

const safeSetAttribute = (element, attribute, value) => {
  try {
    if (element) {
      element.setAttribute(attribute, value);
    }
  } catch (error) {
    console.warn('Safe setAttribute failed:', error);
  }
};

const useToggleStylesheet = (isRTL, isDark) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const mountedRef = useRef(true);
  const linksRef = useRef([]);
  const timeoutRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      // Clear any pending timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Clean up any pending operations
      linksRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!mountedRef.current) return;
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Use a small delay to prevent rapid changes
    timeoutRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      
      setIsLoaded(false);
      
      // Safely remove existing stylesheets with proper cleanup
      const existingLinks = document.getElementsByClassName('theme-stylesheet');
      const linksArray = Array.from(existingLinks);
      
      linksArray.forEach(link => {
        safeRemoveChild(link.parentNode, link);
      });
      
      // Clear the ref
      linksRef.current = [];
      
      if (!mountedRef.current) return;
      
      const link = document.createElement('link');
      link.href = `${process.env.PUBLIC_URL}/css/theme${isRTL ? '.rtl' : ''}.css`;
      link.type = 'text/css';
      link.rel = 'stylesheet';
      link.className = 'theme-stylesheet';

      const userLink = document.createElement('link');
      userLink.href = `${process.env.PUBLIC_URL}/css/user${
        isRTL ? '.rtl' : ''
      }.css`;
      userLink.type = 'text/css';
      userLink.rel = 'stylesheet';
      userLink.className = 'theme-stylesheet';

      // Store references for cleanup
      linksRef.current = [link, userLink];

      link.onload = () => {
        if (mountedRef.current) {
          setIsLoaded(true);
        }
      };

      const head = document.getElementsByTagName('head')[0];
      if (head && mountedRef.current) {
        safeAppendChild(head, link);
        safeAppendChild(head, userLink);
      }
      
      const html = document.getElementsByTagName('html')[0];
      if (html && mountedRef.current) {
        safeSetAttribute(html, 'dir', isRTL ? 'rtl' : 'ltr');
      }
    }, 50); // Small delay to prevent rapid changes
    
    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      if (!mountedRef.current) return;
      
      linksRef.current.forEach(link => {
        safeRemoveChild(link.parentNode, link);
      });
      linksRef.current = [];
    };
  }, [isRTL]);

  useEffect(() => {
    if (!mountedRef.current) return;
    
    // Use a small delay to prevent rapid changes
    timeoutRef.current = setTimeout(() => {
      if (document.documentElement) {
        safeSetAttribute(document.documentElement, 'data-bs-theme', isDark ? 'dark' : 'light');
      }
    }, 50);
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isDark]);

  return { isLoaded };
};

export default useToggleStylesheet;
